const FORBIDDEN_RECORD_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
type SanitizationCircularSource = unknown[] | Record<string, unknown>;

export function isForbiddenRecordKey(key: string): boolean {
  return FORBIDDEN_RECORD_KEYS.has(key);
}

export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isReactValue(value: unknown): boolean {
  return isPlainRecord(value) && typeof value.$$typeof === 'symbol';
}

export function sanitizeRecordValue(
  value: unknown,
  seen: WeakMap<SanitizationCircularSource, unknown> = new WeakMap(),
): unknown {
  if (Array.isArray(value)) {
    const existing = seen.get(value);
    if (existing) {
      return existing;
    }

    const sanitizedItems: unknown[] = [];
    seen.set(value, sanitizedItems);
    for (const item of value) {
      sanitizedItems.push(sanitizeRecordValue(item, seen));
    }
    return sanitizedItems;
  }

  if (!isPlainRecord(value) || isReactValue(value)) {
    return value;
  }

  const existing = seen.get(value);
  if (existing) {
    return existing;
  }

  const sanitizedRecord: Record<string, unknown> = {};
  seen.set(value, sanitizedRecord);
  for (const [key, nestedValue] of Object.entries(value)) {
    if (!isForbiddenRecordKey(key)) {
      sanitizedRecord[key] = sanitizeRecordValue(nestedValue, seen);
    }
  }
  return sanitizedRecord;
}

export function copySanitizedOwnEnumerableProperties(
  target: Record<string, unknown>,
  source: unknown,
): void {
  if (!isPlainRecord(source) || isReactValue(source)) {
    return;
  }

  const seen = new WeakMap<SanitizationCircularSource, unknown>();
  for (const [key, value] of Object.entries(source)) {
    if (!isForbiddenRecordKey(key)) {
      target[key] = sanitizeRecordValue(value, seen);
    }
  }
}

export function cloneSanitizedRecord(source: unknown): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  copySanitizedOwnEnumerableProperties(result, source);
  return result;
}

export function mergeSanitizedRecords(...sources: unknown[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const source of sources) {
    copySanitizedOwnEnumerableProperties(result, source);
  }
  return result;
}

export function omitSanitizedRecordKeys(
  source: Record<string, unknown>,
  keys: Iterable<string>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const keysToOmit = new Set(keys);
  const seen = new WeakMap<SanitizationCircularSource, unknown>();
  for (const [key, value] of Object.entries(source)) {
    if (!keysToOmit.has(key) && !isForbiddenRecordKey(key)) {
      result[key] = sanitizeRecordValue(value, seen);
    }
  }
  return result;
}
